"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import type { PageBlock } from "@/types";
import { CheckIcon } from "@/components/Icons";
import ContactFormBlock from "@/components/blocks/ContactFormBlock";

/**
 * Rendert de blokken van een CMS-pagina. De opmaak spiegelt de secties die
 * eerder hardgecodeerd in de pagina's stonden, zodat het beeld hetzelfde blijft
 * maar de inhoud uit de backend komt.
 */

const kaart: React.CSSProperties = {
  background: "#fff", padding: "36px", borderRadius: "8px", boxShadow: "var(--shadow-sm)",
};

/**
 * De rich-text-editor levert koppen vanaf <h3> (en de sanitizer laat h2-h4 toe),
 * terwijl de pagina al een <h1> heeft: een <h3> direct onder de <h1> slaat een
 * niveau over (HTML-04). Dit schuift de koppen van één tekstblok zo op dat de
 * hoogste kop een <h2> is en de onderlinge verhoudingen blijven kloppen
 * (h3 → h2, h4 → h3). Alleen bij het renderen: de opgeslagen inhoud blijft
 * ongemoeid. De opmaak is gelijk, .cms-tekst geeft h2 en h3 dezelfde stijl.
 */
function kopniveausVoorPagina(html: string): string {
  const niveaus = Array.from(html.matchAll(/<h([2-4])[\s>]/gi), (m) => Number(m[1]));
  if (!niveaus.length) return html;
  const verschuiving = Math.min(...niveaus) - 2;
  if (verschuiving <= 0) return html;
  return html.replace(/<(\/?)h([2-4])(?=[\s>])/gi, (_m, slash: string, n: string) => `<${slash}h${Number(n) - verschuiving}`);
}

function RijkeTekst({ html }: { html?: string }) {
  if (!html) return null;
  return (
    <div
      className="cms-tekst"
      style={{ color: "var(--color-gray-600)", lineHeight: 1.8, fontSize: "0.95rem" }}
      dangerouslySetInnerHTML={{ __html: kopniveausVoorPagina(html) }}
    />
  );
}

function FaqBlok({ titel, items }: { titel?: string; items?: { question: string; answer: string }[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const vragen = (items || []).filter((f) => f?.question);
  if (!vragen.length) return null;
  return (
    <div>
      {titel && (
        <h2 style={{
          fontSize: "1.4rem", fontWeight: 700, fontFamily: "var(--font-serif)",
          color: "var(--color-secondary)", marginBottom: "14px",
        }}>
          {titel}
        </h2>
      )}
      <div style={{ border: "1px solid var(--color-gray-200)", borderRadius: "6px", overflow: "hidden" }}>
      {vragen.map((faq, i) => (
        <div key={i} style={{ borderBottom: i < vragen.length - 1 ? "1px solid var(--color-gray-200)" : undefined }}>
          <button
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
            style={{
              width: "100%", textAlign: "left", padding: "16px 18px", background: "#fff", border: "none",
              cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
              fontWeight: 600, fontSize: "0.92rem", color: "var(--color-secondary)", gap: "12px",
            }}
          >
            {faq.question}
            <span aria-hidden style={{
              flexShrink: 0, width: "9px", height: "9px", borderRight: "2px solid var(--color-gray-400)",
              borderBottom: "2px solid var(--color-gray-400)",
              transform: open === i ? "rotate(-135deg)" : "rotate(45deg)",
              transition: "transform 0.2s", marginTop: open === i ? "4px" : "-4px",
            }} />
          </button>
          {open === i && (
            <div style={{ padding: "0 18px 16px", fontSize: "0.9rem", color: "var(--color-gray-600)", lineHeight: 1.7 }}>
              {faq.answer}
            </div>
          )}
        </div>
      ))}
      </div>
    </div>
  );
}

function Blok({ block }: { block: PageBlock }) {
  switch (block.type) {
    case "hero":
      return (
        <section style={{ textAlign: "center", marginBottom: "12px" }}>
          {block.image && (
            <div style={{ position: "relative", width: "100%", aspectRatio: "3/1", borderRadius: "8px", overflow: "hidden", marginBottom: "28px" }}>
              <Image src={block.image} alt={block.title || ""} fill sizes="(max-width: 900px) 100vw, 1100px" style={{ objectFit: "cover" }} />
            </div>
          )}
          {block.title && (
            <h1 style={{
              fontSize: "2.4rem", fontWeight: 800, color: "var(--color-secondary)",
              fontFamily: "var(--font-serif)", marginBottom: "14px", lineHeight: 1.15,
            }}>
              {block.title}
            </h1>
          )}
          {block.subtitle && (
            <p style={{ fontSize: "1.05rem", color: "var(--color-gray-500)", maxWidth: "700px", margin: "0 auto", lineHeight: 1.7 }}>
              {block.subtitle}
            </p>
          )}
        </section>
      );

    case "tekst":
      return <div style={kaart}><RijkeTekst html={block.html} /></div>;

    case "tekstBeeld": {
      const beeldLinks = block.imagePositie === "links";
      return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "36px", alignItems: "center" }}>
          {block.image && (
            <div style={{ position: "relative", aspectRatio: "4/3", borderRadius: "8px", overflow: "hidden", order: beeldLinks ? 0 : 1 }}>
              <Image src={block.image} alt="" fill sizes="(max-width: 900px) 100vw, 520px" style={{ objectFit: "cover" }} />
            </div>
          )}
          <div style={{ order: beeldLinks ? 1 : 0 }}><RijkeTekst html={block.html} /></div>
        </div>
      );
    }

    case "usp": {
      const items = (block.items || []).filter((u) => u?.titel || u?.tekst);
      if (!items.length) return null;
      return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" }}>
          {items.map((u, i) => (
            <div key={i} style={{ ...kaart, padding: "24px", textAlign: "center" }}>
              <CheckIcon size="1.4rem" style={{ color: "var(--color-primary)", marginBottom: "10px" }} />
              {u.titel && <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--color-secondary)", marginBottom: "6px", overflowWrap: "break-word" }}>{u.titel}</div>}
              {u.tekst && <div style={{ fontSize: "0.85rem", color: "var(--color-gray-500)", lineHeight: 1.6, overflowWrap: "break-word" }}>{u.tekst}</div>}
            </div>
          ))}
        </div>
      );
    }

    case "faq":
      return <FaqBlok titel={block.titel} items={block.items} />;

    case "cta":
      if (!block.titel && !block.knopTekst) return null;
      return (
        <div style={{ ...kaart, textAlign: "center", background: "var(--color-nude, #f6efe8)" }}>
          {block.titel && (
            <h2 style={{ fontSize: "1.4rem", fontWeight: 700, fontFamily: "var(--font-serif)", color: "var(--color-secondary)", marginBottom: "10px", overflowWrap: "break-word", wordBreak: "break-word" }}>
              {block.titel}
            </h2>
          )}
          {block.tekst && (
            <p style={{ color: "var(--color-gray-600)", fontSize: "0.95rem", lineHeight: 1.7, maxWidth: "620px", margin: "0 auto 20px", overflowWrap: "break-word" }}>
              {block.tekst}
            </p>
          )}
          {block.knopTekst && block.knopLink && (
            <Link href={block.knopLink} className="btn btn-primary" style={{ display: "inline-block", padding: "12px 28px" }}>
              {block.knopTekst}
            </Link>
          )}
        </div>
      );

    case "logoSlider":
      return <div className="logo-slider-placeholder" style={{ borderTop: "1px solid var(--color-gray-200)", paddingTop: "24px" }} />;

    case "contactformulier":
      return <ContactFormBlock />;

    case "html":
      // Zelfde kaart + "cms-tekst" typografieklasse als het tekst-blok —
      // anders komt dit ontsnappingsluik (bv. een tabel die de rich-text-
      // editor niet kan maken) los en ongestyled tussen de andere blokken te
      // staan.
      return block.html ? (
        <div style={kaart}>
          <div className="cms-tekst" dangerouslySetInnerHTML={{ __html: kopniveausVoorPagina(block.html) }} />
        </div>
      ) : null;

    default:
      return null;
  }
}

export default function PageBlocks({ blocks }: { blocks: PageBlock[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
      {(blocks || []).map((b, i) => <Blok key={i} block={b} />)}
    </div>
  );
}
